// src/js/mc/chatlog.ts
// Phase 251 (251-08, D-14/D-15/D-17/D-19) — the DM-thread render core for the server-detail
// page's Chat section: fetches the newest page of the merged thread and turns it into
// aligned, grouped, avatar-headed bubbles with localised per-cluster timestamps and exactly
// one of the three coverage states. Every clustering/coverage/dedup decision lives in the
// zero-import `utils/chatThread` module (node-testable, no bundler needed); this file is thin
// DOM assembly on top of it, mirroring how `feed.ts` is thin DOM assembly on top of
// `utils/feedTail.ts`.
//
// "Load older messages", the "new messages" pill, and the compose-box submit handler are left
// entirely alone here — Plan 251-09 wires them. This file's module-scoped cursor/hasMore state
// is declared now purely so that plan is additive, not a second parallel state store.
//
// T-251-01 (this module's single most important line, repeated at every text-bearing node
// below): every bubble/name/timestamp node is populated with .textContent or a plain string
// argument to setAttribute() — never a raw-markup DOM sink. Both player chat and the operator's
// own /say echo are untrusted by the time they reach this file.

import { localizeInstant } from '../utils/instants';
import { showToast } from './controls';
import { appendNarrativeSegments } from './coordPill';
import {
    clusterRows,
    coverageState,
    selectUnseenRows,
    recordSeenKeys,
    formatCoverageDate,
    startsNewCluster,
    shouldAutoFollow,
    preservedScrollTop,
    tailSinceIso,
    pillLabel,
    localDayKey,
    isNewLocalDay,
    localDayLabel,
    THREAD_ROW_CAP,
    type ChatRowLike,
    type ChatCoverageLike,
    type ChatCluster,
} from '../utils/chatThread';

// ---------------------------------------------------------------------------------------
// Wire shapes (McChatModel::getChatlog()'s JSON — app/Models/McChatModel.php)
// ---------------------------------------------------------------------------------------

interface ChatlogPayload {
    rows: ChatRowLike[];
    has_more: boolean;
    oldest_cursor: string | null;
    coverage: ChatCoverageLike;
}

// ---------------------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------------------

// Chat is watched live the same way the console stream is, so it needs to feel just as
// immediate. The console gets that from a genuine push (a WebSocket straight to Pterodactyl,
// see console.ts); chat has no such push path — it's a poll — so instead it gets its own much
// faster cadence, deliberately NOT the shared utils/poll.ts POLL_INTERVAL (30s) that the
// fleet grid / detail poll / feed loop share, since those aren't watched turn-by-turn the way
// an open chat thread is.
const CHAT_TAIL_INTERVAL_MS = 3_000;

// WR-01-style idempotency guard (feedback_shared_component_every_page_init_double_bind) — the
// exact idiom resourceHistory.ts's own initializedContainers WeakSet already uses, so a second
// initDetail() call on the same page cannot bind a second fetch or duplicate rows.
const initializedSections = new WeakSet<HTMLElement>();

// Per-section thread state. Module-scoped rather than per-call because this page mounts at
// most one .mc-chatlog-section per full load — the same single-instance module-state
// convention controls.ts/feed.ts already use.
const chatSeenKeys = new Set<string>();
let chatOldestCursor: string | null = null;
let chatHasMore = false;

// 251-09 additions -- Load-older in-flight/loop-guard state (T-251-44).
let chatLoadOlderInFlight = false;
let chatLoadOlderExhausted = false;

// 251-09 additions -- live-tail baseline + pause/resume timer state, mirroring mc.ts's own
// detailPollTimer/scheduleDetailPoll shape (see bindLiveTail()/runTailPoll() below).
let chatTailTimer: ReturnType<typeof setTimeout> | null = null;
// Advances ONLY from a tail response's own rows -- never from the initial page fetch, so
// chatInitialIso (below) stays the sole fallback baseline until the very first tail tick that
// actually returns something.
let chatNewestSeenMs: number | null = null;
// The newest row's instant from the INITIAL page fetch, captured once. The only baseline the
// tail's very first tick can use before chatNewestSeenMs has ever been set.
let chatInitialIso: string | null = null;
// Count of messages appended to the thread while the reader was scrolled away (UI-SPEC §8.7's
// "New messages" pill), reset to 0 when the pill is clicked.
let chatQueuedCount = 0;

// The most-recently-rendered row/cluster in the thread (bottom-most), so a live-tail append (or
// the compose echo below) can decide whether to merge into that cluster or start a new one --
// startsNewCluster() needs the actual PREVIOUS row, not just "the last cluster's kind".
let chatLastRow: ChatRowLike | null = null;
let chatLastClusterEl: HTMLElement | null = null;

// ---------------------------------------------------------------------------------------
// Compose lifecycle state (D-11/D-12/D-13, UI-SPEC §8.5/§8.6)
// ---------------------------------------------------------------------------------------

/** A successfully-ACKed optimistic echo, tracked only long enough for the next tail poll(s) to
 *  reconcile it against the server's own `chat_say` row instead of rendering a second bubble
 *  (T-251-43). */
interface PendingEcho {
    content: string;
    sentAtMs: number;
    bubbleEl: HTMLElement;
}

/** Expires an unmatched echo after two poll intervals -- also doubles as the match window
 *  (a `chat_say` row landing anywhere within this span of its own optimistic send is treated as
 *  the same message). */
const PENDING_ECHO_TTL_MS = 2 * CHAT_TAIL_INTERVAL_MS;

let chatPendingEchoes: PendingEcho[] = [];

// ---------------------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------------------

export function initChatlog(container: HTMLElement): void {
    const section = container.querySelector<HTMLElement>('.mc-chatlog-section');
    if (!section) return; // Section not present on this page render.

    if (initializedSections.has(section)) return;
    initializedSections.add(section);

    const url = section.dataset.chatlogUrl;
    if (!url) return;

    void loadInitialPage(section, url);

    // 251-09: Load-older, the live tail and the "new messages" pill all bind unconditionally at
    // init, independent of the initial fetch's own success/failure -- a failed initial fetch
    // (renderFetchError()) should not also silently disable a reader's ability to retry via
    // scroll-driven pagination once the section is otherwise usable again.
    bindLoadOlder(section, url);
    bindNewMessagesPill(section);
    bindLiveTail(section);
    bindCompose(section);
}

// ---------------------------------------------------------------------------------------
// Initial fetch
// ---------------------------------------------------------------------------------------

async function loadInitialPage(section: HTMLElement, url: string): Promise<void> {
    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: unknown = await resp.json();
        if (payload === null || typeof payload !== 'object') {
            throw new Error('malformed chatlog payload');
        }
        const typed = payload as ChatlogPayload;

        renderCoverage(section, typed.coverage);
        const clusters = renderRows(section, typed.rows);

        if (clusters.length > 0) {
            const lastCluster = clusters[clusters.length - 1];
            chatLastRow = lastCluster.rows[lastCluster.rows.length - 1];
            chatLastClusterEl =
                (section.querySelector<HTMLElement>('.mc-chatlog-rows')?.lastElementChild as HTMLElement | null) ??
                null;
        }

        // UI-SPEC §8.7 live tail: the newest row on the initial page is the baseline for the
        // tail's very first tick -- chatNewestSeenMs itself only ever advances from a tail
        // response, never from this initial fetch (see its own declaration comment above). The
        // wire delivers rows newest-first, so typed.rows[0] is that newest row.
        //
        // Post-code-review fix (251-REVIEW.md WR-03): when the initial page has ZERO rows (a
        // brand-new server, or any server before its first message -- UI-SPEC §8.8 state A),
        // fall back to the page-load instant rather than null. Both chatInitialIso and
        // chatNewestSeenMs are otherwise permanently null in that case, and runTailPoll() skips
        // its request entirely whenever both are null -- a self-sustaining deadlock where no
        // message that arrives after page load would EVER appear without a manual reload, for
        // the tab's whole lifetime, even though this is a common first-use scenario.
        chatInitialIso =
            typed.rows.length > 0 ? tailSinceIso(typed.rows[0].occurred_at_ms) : tailSinceIso(Date.now());

        chatOldestCursor = typed.oldest_cursor;
        chatHasMore = typed.has_more;
        updateLoadOlderVisibility(section);

        // UI-SPEC §8.7: newest message visible on load.
        const thread = section.querySelector<HTMLElement>('.mc-chatlog-thread');
        if (thread) thread.scrollTop = thread.scrollHeight;
    } catch (err) {
        // Never leave the thread in a silent blank state on a fetch failure.
        console.error('[mc-chatlog] initial fetch error:', err);
        renderFetchError(section);
    }
}

// ---------------------------------------------------------------------------------------
// Row rendering
// ---------------------------------------------------------------------------------------

function renderRows(section: HTMLElement, rawRows: ChatRowLike[]): ChatCluster[] {
    const rowsContainer = section.querySelector<HTMLElement>('.mc-chatlog-rows');
    if (!rowsContainer) return [];

    // The wire delivers rows NEWEST-first (McChatModel::fetchMerged()'s own
    // `ORDER BY occurred_at DESC`). chatThread.ts's clusterRows() requires OLDEST-first input
    // per its own signature comment — reverse here, at the caller, exactly as that comment
    // says to. Feeding the wire order in unreversed would silently produce plausible-looking
    // but chronologically-backwards clusters.
    const oldestFirst = [...rawRows].reverse();

    const unseen = selectUnseenRows(oldestFirst, chatSeenKeys);
    recordSeenKeys(unseen.map((row) => row.row_key), chatSeenKeys);

    // UI-SPEC §8.9: a content-less row is silently omitted, never rendered as a
    // "(message unavailable)" placeholder — this codebase deliberately has no such pattern.
    const withContent = unseen.filter((row) => typeof row.content === 'string' && row.content !== '');
    if (withContent.length === 0) return [];

    const clusters = clusterRows(withContent);
    let prevMs: number | null = null;
    for (const cluster of clusters) {
        const firstMs = cluster.rows[0].occurred_at_ms;
        if (isNewLocalDay(prevMs, firstMs)) {
            rowsContainer.appendChild(dayDividerEl(firstMs));
        }
        rowsContainer.appendChild(buildClusterEl(cluster));
        prevMs = cluster.rows[cluster.rows.length - 1].occurred_at_ms;
    }
    return clusters;
}

/** Checkpoint polish (post-251-09): a centred "Today" / "Yesterday" / dated day-boundary
 *  heading, DM-app style. `Date.now()` is read HERE, at the DOM layer, at render time -- never
 *  inside chatThread.ts's pure localDayLabel(), which takes it as an explicit parameter. */
function dayDividerEl(ms: number): HTMLElement {
    const el = document.createElement('div');
    el.className = 'mc-chatlog-day-divider';
    el.textContent = localDayLabel(ms, Date.now());
    return el;
}

/** A bubble's position within its own cluster's consecutive run — drives the connected-stack
 *  corner-radius treatment (checkpoint ask: "shouldn't just be the same border-radius all around
 *  on all bubbles"), matching common DM-app grouping. 'only' gets the full default radius on
 *  every corner (no modifier class at all). */
type BubbleGroupPosition = 'only' | 'first' | 'middle' | 'last';

function bubblePosition(index: number, total: number): BubbleGroupPosition {
    if (total === 1) return 'only';
    if (index === 0) return 'first';
    if (index === total - 1) return 'last';
    return 'middle';
}

// UI-SPEC §8.12 / D-14: the literal shown for the operator's own messages, in the accessible
// tree (aria-label) as well as visually absent-but-implied by right-alignment. Declared once here
// so buildClusterEl(), the live-tail append path in appendClusters(), and Task 3's
// reconcilePendingEchoes() share exactly one copy of this user-visible literal rather than three
// independent copies drifting apart.
const CHAT_OUTGOING_SENDER_LABEL = 'You';

/**
 * The single shared bubble-CONTENT renderer — T-251-01's own text-node-only discipline lives HERE
 * and nowhere else. Both `buildBubbleEl()` (fresh build) and `reconcilePendingEchoes()` (Task 3's
 * D-13 re-render on reconciliation) call this one function rather than each carrying its own copy
 * of the glyph/segment composition.
 *
 * Phase 274 (D-01/CHAT-01/CHAT-02, D-03, D-04): a row carrying `narrative_segments` renders
 * through the shared, UNCHANGED `coordPill.ts`'s `appendNarrativeSegments()` (Rule A, `mode:
 * 'link'` — a coord segment degrades to plain text, never an untinted pill, when its `href` is
 * null). A row without segments (the pre-274 fallback, or simply a non-waypoint message) falls
 * back to today's exact behaviour byte-for-byte. Both player chat and the operator's own `/say`
 * echo are untrusted at render time, so every text-bearing node here is a text node or a plain
 * string argument to `setAttribute` — never a raw-markup DOM sink (T-251-01).
 */
function renderBubbleContent(bubble: HTMLElement, row: ChatRowLike, senderLabel: string): void {
    // The compose-echo path (bindCompose()) appends a `.mc-chatlog-ack` node into the bubble AFTER
    // buildBubbleEl() returns — a re-render (D-13) must not destroy the operator's own send
    // acknowledgement. Detach and hold it here, then re-append it below.
    const ackEl = bubble.querySelector('.mc-chatlog-ack');

    bubble.replaceChildren();
    // IN-06 — `replaceChildren()` clears children but NOT attributes. `title` is additive and
    // must never survive from a previous render into a row that should not carry one, so it is
    // cleared here, once, and re-set below only by the one branch that wants it. Clearing it in
    // this single place (rather than in each `else` arm) is what keeps the two exit paths from
    // drifting apart the next time this function grows a branch.
    bubble.removeAttribute('title');

    if (Array.isArray(row.narrative_segments) && row.narrative_segments.length > 0) {
        appendNarrativeSegments(bubble, row.narrative_segments, { mode: 'link' });
    }

    // WR-01 — branch on ACTUAL RENDERED OUTPUT, never on the input array's length.
    //
    // `appendNarrativeSegments()` (coordPill.ts) SILENTLY SKIPS any element it does not
    // recognise: a non-object, a non-string `text.value`, a non-finite coord, an unknown `type`.
    // A segment array that is non-empty but wholly unrenderable therefore produced an EMPTY
    // bubble under a length check — the message body vanished, and the documented `row.content`
    // fallback was unreachable because it lived in the `else` arm the length check had already
    // taken. The `aria-label` was still set, so a screen reader announced text a sighted user
    // could not see.
    //
    // That deploy-window / future-wire-shape mismatch is the exact scenario `narrative_segments`
    // is optional FOR (see chatThread.ts's own field docblock), so the fallback has to be driven
    // by what actually landed in the DOM. `ackEl` was detached by `replaceChildren()` above and
    // is not re-appended until the end, so it cannot mask an otherwise-empty bubble here.
    if (bubble.childNodes.length === 0) {
        // No segments, or none of them renderable — today's exact, unchanged plain-text
        // behaviour, and no `title` (already cleared above).
        bubble.appendChild(document.createTextNode(row.content));
        bubble.setAttribute('aria-label', `${senderLabel}: ${row.content}`);
    } else {
        // The `?? row.content` arm is the deploy-window fallback for a backend that emits
        // segments but not the aria sentence.
        bubble.setAttribute('aria-label', `${senderLabel}: ${row.waypoint_aria ?? row.content}`);
        if (typeof row.waypoint_raw === 'string' && row.waypoint_raw !== '') {
            // D-03 — the raw xaero-waypoint: string, preserved via a DOM property/setAttribute()
            // (never an HTML-parsing sink).
            bubble.title = row.waypoint_raw;
        }
    }

    if (ackEl) {
        bubble.appendChild(ackEl);
    }
}

/**
 * The single per-row bubble builder. Every growth path (initial render, Load-older prepend,
 * live-tail append, and the compose echo) goes through this one function, never a second copy of
 * the same className assembly.
 */
function buildBubbleEl(
    row: ChatRowLike,
    outgoing: boolean,
    senderLabel: string,
    groupPosition: BubbleGroupPosition,
): HTMLElement {
    const bubble = document.createElement('div');
    const alignClass = outgoing ? 'mc-chatlog-bubble--outgoing' : 'mc-chatlog-bubble--incoming';
    const posClass = groupPosition === 'only' ? '' : ` mc-chatlog-bubble--${groupPosition}`;
    bubble.className = `mc-chatlog-bubble ${alignClass}${posClass}`;
    renderBubbleContent(bubble, row, senderLabel);
    return bubble;
}

function buildClusterEl(cluster: ChatCluster): HTMLElement {
    const outgoing = cluster.kind === 'say';
    const lastRow = cluster.rows[cluster.rows.length - 1];

    const clusterEl = document.createElement('div');
    clusterEl.className = outgoing
        ? 'mc-chatlog-cluster mc-chatlog-cluster--outgoing'
        : 'mc-chatlog-cluster mc-chatlog-cluster--incoming';
    // Checkpoint polish: the day-divider adjacency check (Load-older prepend, live-tail append)
    // reads this back off whichever cluster currently sits at either end of the DOM, rather than
    // tracking a second parallel "last rendered day" state variable.
    clusterEl.dataset.day = localDayKey(cluster.rows[0].occurred_at_ms);

    // UI-SPEC §8.12: the sender context stays in the accessible tree on EVERY bubble in the
    // cluster, even though it is shown visually only once (on the meta row).
    const senderLabel = outgoing ? CHAT_OUTGOING_SENDER_LABEL : playerDisplayName(cluster.player_name);

    if (outgoing) {
        // D-14/UI-SPEC §8.3: outgoing clusters get NO avatar/name — there is exactly one operator
        // in this thread and right-alignment alone identifies them. A timestamp-only meta row
        // still sits above the bubbles, matching the incoming layout's own "meta above messages"
        // shape (checkpoint ask).
        clusterEl.appendChild(buildMetaEl(cluster, lastRow, true));
        for (let i = 0; i < cluster.rows.length; i++) {
            const position = bubblePosition(i, cluster.rows.length);
            clusterEl.appendChild(buildBubbleEl(cluster.rows[i], true, senderLabel, position));
        }
    } else {
        // Checkpoint polish: avatar in its own left column, aligned to the cluster's first
        // message (not repeated per-bubble) — the classic DM/Slack/Discord layout, replacing the
        // original stacked avatar-over-messages header. This also structurally fixes the
        // resolved-vs-unresolved vertical-alignment bug the operator flagged: avatar and name are
        // no longer siblings inside one flex row (where a resolved row's <a>-wrapped name and an
        // unresolved row's bare <span> name could pick up different line-heights) — the avatar
        // now lives in its own column entirely, and the name lives in the meta row instead.
        const avatarCol = document.createElement('div');
        avatarCol.className = 'mc-chatlog-avatar-col';
        avatarCol.appendChild(chatAvatarEl(cluster.player_uuid));

        const body = document.createElement('div');
        body.className = 'mc-chatlog-cluster-body';
        body.appendChild(buildMetaEl(cluster, lastRow, false));
        for (let i = 0; i < cluster.rows.length; i++) {
            const position = bubblePosition(i, cluster.rows.length);
            body.appendChild(buildBubbleEl(cluster.rows[i], false, senderLabel, position));
        }

        clusterEl.appendChild(avatarCol);
        clusterEl.appendChild(body);
    }

    return clusterEl;
}

/** The name+timestamp meta row shown once per cluster, above its messages (checkpoint ask —
 *  previously a below-cluster timestamp-only footer). Incoming clusters show name + time;
 *  outgoing clusters show time only (no name — right-alignment alone identifies the operator). */
function buildMetaEl(cluster: ChatCluster, lastRow: ChatRowLike, outgoing: boolean): HTMLElement {
    const meta = document.createElement('div');
    meta.className = outgoing
        ? 'mc-chatlog-cluster-meta mc-chatlog-cluster-meta--outgoing'
        : 'mc-chatlog-cluster-meta';

    if (!outgoing) {
        meta.appendChild(chatNameEl(cluster.player_uuid, cluster.player_name));
    }
    meta.appendChild(buildMetaTimeEl(lastRow));

    return meta;
}

/** One localised timestamp per cluster (UI-SPEC §8.4), populated at creation time — never a
 *  hand-formatted date string, never waiting for the 60s ticker. Lives inside the meta row. */
function buildMetaTimeEl(lastRow: ChatRowLike): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = 'mc-chatlog-time';

    const timeEl = document.createElement('time');
    timeEl.dataset.utc = new Date(lastRow.occurred_at_ms).toISOString();
    localizeInstant(timeEl); // populate on creation, don't wait for the 60s ticker.

    wrap.appendChild(timeEl);
    return wrap;
}

/** Falls back to the "Unknown player" literal for a null/empty resolved name — never an empty
 *  identity row (D-19's failed-resolution fallback discipline). */
function playerDisplayName(name: string | null): string {
    return name !== null && name !== '' ? name : 'Unknown player';
}

// ---------------------------------------------------------------------------------------
// Identity rendering (RESEARCH Pitfall 4 — NEW logic, not a reuse of feed.ts's own
// uuid-required avatar helper)
// ---------------------------------------------------------------------------------------

/**
 * Builds a player-chat cluster's avatar tile, standing alone in its own left-hand column
 * (checkpoint polish — previously shared a flex row with the name, which produced the
 * resolved-vs-unresolved vertical-misalignment the operator flagged). Deliberately NOT a call
 * into this codebase's existing avatar-tile helper (feed.ts) — that helper takes uuid as a
 * REQUIRED, non-nullable parameter and builds both the mc-heads.net URL and the profile href
 * directly from it, so passing a null uuid there would request an avatar for the literal string
 * "null" and link to a "null" profile.
 */
function chatAvatarEl(uuid: string | null): HTMLElement {
    const tile = document.createElement('span');
    tile.className = 'mc-avatar-tile';

    if (uuid === null || uuid === '') {
        // D-19 unresolved-backfill fallback — an EMPTY tile (no <img> at all, so the CSS
        // placeholder renders and no third-party request is ever issued -- T-251-10), no link.
        return tile;
    }

    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(uuid)}/32`;
    img.loading = 'lazy';
    img.width = 32;
    img.height = 32;
    img.alt = '';
    img.className = 'mc-player-avatar';
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
    tile.appendChild(img);

    const link = document.createElement('a');
    link.href = `/mc/players/${encodeURIComponent(uuid)}`;
    link.title = 'View profile';
    link.className = 'mc-chatlog-avatar-link';
    link.appendChild(tile);
    return link;
}

/** The player name shown in the meta row above a cluster's messages (checkpoint polish — split
 *  out of the old combined avatar+name chatSenderEl()). Linked to the player's profile when the
 *  uuid resolved; a plain unlinked span for D-19's unresolved-backfill fallback. */
function chatNameEl(uuid: string | null, name: string | null): HTMLElement {
    const displayName = playerDisplayName(name);

    if (uuid !== null && uuid !== '') {
        const link = document.createElement('a');
        link.href = `/mc/players/${encodeURIComponent(uuid)}`;
        link.className = 'mc-chatlog-cluster-name';
        link.textContent = displayName;
        return link;
    }

    const span = document.createElement('span');
    span.className = 'mc-chatlog-cluster-name';
    span.textContent = displayName;
    return span;
}

// ---------------------------------------------------------------------------------------
// Coverage rendering (D-05/D-06/D-07, UI-SPEC §8.8)
// ---------------------------------------------------------------------------------------

function renderCoverage(section: HTMLElement, coverage: ChatCoverageLike): void {
    const notice = section.querySelector<HTMLElement>('.mc-chatlog-notice');
    const rowsContainer = section.querySelector<HTMLElement>('.mc-chatlog-rows');

    // Clear both slots FIRST -- structurally guarantees a re-render can never leave a stale
    // note beside a new divider (UI-SPEC §8.8: exactly one of the three states renders, never
    // a badge, never two at once).
    if (notice) {
        notice.hidden = true;
        notice.textContent = '';
    }
    rowsContainer?.querySelector<HTMLElement>('.mc-chatlog-divider')?.remove();

    const state = coverageState(coverage);

    if (state === 'never-logged') {
        if (notice) {
            notice.hidden = false;
            notice.textContent =
                "Chat logging not enabled for this server yet — only the operator's messages below are recorded.";
        }
        return;
    }

    if (state === 'since-boundary') {
        if (rowsContainer && coverage.earliest_ms !== null) {
            const divider = document.createElement('div');
            divider.className = 'mc-chatlog-divider';
            divider.textContent = `Chat logging since ${formatCoverageDate(coverage.earliest_ms)}`;
            rowsContainer.insertBefore(divider, rowsContainer.firstChild);
        }
        return;
    }

    // state === 'complete' -- render NEITHER, matching renderCoverageSubtitle()'s (resourceHistory.ts)
    // own blank-when-nothing-is-missing precedent. Both slots were already cleared above.
}

// ---------------------------------------------------------------------------------------
// Load older messages (UI-SPEC §8.7) — top-anchored pagination, inverted from feed.ts's own
// bottom-anchored "Load more" (feed.ts's bindLoadMore()/prependStreamRows()/showLoadMore()/
// hideLoadMore() are the precedent this section mirrors, just applied to the opposite edge).
// ---------------------------------------------------------------------------------------

function updateLoadOlderVisibility(section: HTMLElement): void {
    const btn = section.querySelector<HTMLButtonElement>('.mc-chatlog-load-older');
    if (btn) btn.hidden = !chatHasMore;
}

function bindLoadOlder(section: HTMLElement, chatlogUrl: string): void {
    const btn = section.querySelector<HTMLButtonElement>('.mc-chatlog-load-older');
    if (!btn) return;

    // Rule 1 fix (discovered via live browser verification, not merely reasoned about): the
    // button sits at the TOP of a scrollable, tabindex="0" .mc-chatlog-thread. A mouse click on
    // a <button> focuses it BEFORE the 'click' event fires, and focusing an element inside a
    // scrollable container triggers the browser's own native scroll-into-view-on-focus behavior
    // — synchronously snapping the reader's scroll position back toward the button (near the
    // top) before loadOlderPage() ever runs, so the prepend-compensation helper below would
    // compute from an already-corrupted prevScrollTop. Suppressing focus on mousedown (a standard, purely
    // mouse-path technique) prevents that native scroll without affecting keyboard activation —
    // a Tab-focused button still receives 'click' from Enter/Space normally, since keyboard
    // focus never goes through 'mousedown' at all.
    btn.addEventListener('mousedown', (e) => e.preventDefault());

    btn.addEventListener('click', () => {
        void loadOlderPage(section, chatlogUrl, btn);
    });
}

async function loadOlderPage(section: HTMLElement, chatlogUrl: string, btn: HTMLButtonElement): Promise<void> {
    if (chatLoadOlderInFlight || chatLoadOlderExhausted || chatOldestCursor === null) return;

    chatLoadOlderInFlight = true;
    btn.disabled = true;

    try {
        // Appended to the template-supplied data-chatlog-url, never a hand-built signal-query string.
        const url = `${chatlogUrl}&before=${encodeURIComponent(chatOldestCursor)}`;
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: unknown = await resp.json();
        if (payload === null || typeof payload !== 'object') {
            throw new Error('malformed chatlog payload');
        }
        const typed = payload as ChatlogPayload;

        const oldestFirst = [...typed.rows].reverse();
        const unseen = selectUnseenRows(oldestFirst, chatSeenKeys);

        if (unseen.length === 0) {
            // T-251-44: the backend's inclusive `<=` boundary re-delivers the boundary row on
            // every request that uses it as `before` — zero NEW rows on this page means every
            // row here has already been rendered, i.e. there is nothing further back to reach.
            // Hide the button permanently for this page load; this is the client-side loop guard
            // that makes the inclusive boundary safe.
            chatLoadOlderExhausted = true;
            chatHasMore = false;
            btn.hidden = true;
            return;
        }

        recordSeenKeys(unseen.map((row) => row.row_key), chatSeenKeys);

        // UI-SPEC §8.9: content-less rows (pre-boundary history) are silently omitted here too.
        const withContent = unseen.filter((row) => typeof row.content === 'string' && row.content !== '');
        const rowsContainer = section.querySelector<HTMLElement>('.mc-chatlog-rows');
        const thread = section.querySelector<HTMLElement>('.mc-chatlog-thread');

        if (withContent.length > 0 && rowsContainer && thread) {
            // Recorded BEFORE insertion — the prepend-compensation helper below needs this
            // pre-prepend geometry to compute the delta. Deliberately NOT also wrapping
            // renderCoverage() (called below): that function's divider must land ABOVE the
            // rows this prepend is about to insert (it marks the coverage boundary, which is
            // chronologically older than everything already on screen), so it has to run AFTER
            // the prepend places those rows at the new top — running it first would insert the
            // divider between the old and new content instead of above all of it.
            const prevScrollHeight = thread.scrollHeight;
            const prevScrollTop = thread.scrollTop;

            const clusters = clusterRows(withContent);
            const fragment = document.createDocumentFragment();
            let prevMs: number | null = null;
            for (const cluster of clusters) {
                const firstMs = cluster.rows[0].occurred_at_ms;
                if (isNewLocalDay(prevMs, firstMs)) {
                    fragment.appendChild(dayDividerEl(firstMs));
                }
                fragment.appendChild(buildClusterEl(cluster));
                prevMs = cluster.rows[cluster.rows.length - 1].occurred_at_ms;
            }
            // Boundary check: does the last (newest) cluster in THIS batch share a day with
            // whatever cluster currently sits at the top of the thread? If not, one more divider
            // marks the transition before the older batch meets the existing newer content.
            const existingFirstDay =
                rowsContainer.querySelector<HTMLElement>('.mc-chatlog-cluster')?.dataset.day ?? null;
            if (existingFirstDay !== null && prevMs !== null && localDayKey(prevMs) !== existingFirstDay) {
                fragment.appendChild(dayDividerEl(prevMs));
            }
            rowsContainer.prepend(fragment);

            // Reuses the SAME clear-both-slots-first function the initial render calls, rather
            // than a second divider-insertion code path. coverage is a fixed per-server fact
            // (identical on every fetch), so calling this again is idempotent and, since it
            // always removes any existing divider before deciding again, this both prevents ever
            // rendering two and repositions the divider at the new top of whatever is now
            // loaded (the rows just prepended above).
            renderCoverage(section, typed.coverage);

            // Prepend compensation — called AFTER both insertions above (never before: the delta
            // only exists once the new content, including any divider reposition, has actually
            // grown the container's scrollHeight).
            thread.scrollTop = preservedScrollTop(prevScrollHeight, prevScrollTop, thread.scrollHeight);

            // A thread that started with zero rendered clusters (e.g. the initial page was
            // entirely content-less) gets its live-tail grouping anchor from here instead, once
            // real history finally loads.
            if (chatLastRow === null) {
                const lastCluster = clusters[clusters.length - 1];
                chatLastRow = lastCluster.rows[lastCluster.rows.length - 1];
                chatLastClusterEl = rowsContainer.lastElementChild as HTMLElement | null;
            }
        } else {
            // No new bubbles this page (every unseen row was content-null) — still safe/idempotent
            // to reconcile the coverage slots even though nothing visually changed, matching every
            // other call site's discipline of never leaving a stale notice beside a divider.
            renderCoverage(section, typed.coverage);
        }

        chatOldestCursor = typed.oldest_cursor;
        chatHasMore = typed.has_more;
        updateLoadOlderVisibility(section);
    } catch (err) {
        console.error('[mc-chatlog] load-older fetch error:', err);
    } finally {
        chatLoadOlderInFlight = false;
        if (!chatLoadOlderExhausted) btn.disabled = false;
    }
}

// ---------------------------------------------------------------------------------------
// Live tail + "new messages" pill (UI-SPEC §8.7)
// ---------------------------------------------------------------------------------------

function bindNewMessagesPill(section: HTMLElement): void {
    const pill = section.querySelector<HTMLButtonElement>('.mc-chatlog-new-messages');
    if (!pill) return;

    pill.addEventListener('click', () => {
        const thread = section.querySelector<HTMLElement>('.mc-chatlog-thread');
        if (thread) thread.scrollTop = thread.scrollHeight;
        chatQueuedCount = 0;
        updateNewMessagesPill(section);
    });
}

function updateNewMessagesPill(section: HTMLElement): void {
    const pill = section.querySelector<HTMLButtonElement>('.mc-chatlog-new-messages');
    if (!pill) return;

    if (chatQueuedCount === 0) {
        pill.hidden = true;
        pill.textContent = '';
    } else {
        pill.hidden = false;
        pill.textContent = pillLabel(chatQueuedCount);
    }
}

function bindLiveTail(section: HTMLElement): void {
    const tailUrl = section.dataset.chatlogTailUrl;
    if (!tailUrl) return;

    const scheduleTailPoll = (): void => {
        chatTailTimer = setTimeout(() => {
            void runTailPoll(section, tailUrl, scheduleTailPoll);
        }, CHAT_TAIL_INTERVAL_MS);
    };

    scheduleTailPoll();

    // Pause while the tab is hidden, immediate re-poll on return — the SAME lifecycle
    // initDetail()'s own detail poll already uses (mc.ts), so a hidden tab stops polling.
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (chatTailTimer !== null) {
                clearTimeout(chatTailTimer);
                chatTailTimer = null;
            }
        } else {
            void runTailPoll(section, tailUrl, scheduleTailPoll);
        }
    });
}

async function runTailPoll(section: HTMLElement, tailUrl: string, reschedule: () => void): Promise<void> {
    if (chatTailTimer !== null) {
        clearTimeout(chatTailTimer);
        chatTailTimer = null;
    }

    const since = tailSinceIso(chatNewestSeenMs) ?? chatInitialIso;
    if (since !== null) {
        try {
            const url = `${tailUrl}&since=${encodeURIComponent(since)}`;
            const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (resp.ok) {
                const payload: unknown = await resp.json();
                if (
                    payload !== null &&
                    typeof payload === 'object' &&
                    Array.isArray((payload as { rows?: unknown }).rows)
                ) {
                    applyTailPayload(section, (payload as { rows: ChatRowLike[] }).rows);
                }
            }
        } catch (err) {
            console.error('[mc-chatlog] tail fetch error:', err);
        }
    }
    // since === null: no baseline exists at all yet (a genuinely empty thread with no tail
    // activity and no initial rows) — skip this tick's request entirely rather than giving the
    // server an arbitrary window (this is exactly the case UI-SPEC §8.7's own reasoning calls
    // out: with no baseline the server would have to be given an arbitrary window).

    reschedule();
}

function applyTailPayload(section: HTMLElement, rawRows: ChatRowLike[]): void {
    // T-251-43 — run BEFORE the normal dedup/render pipeline: a matched row is marked seen here
    // so the pipeline below skips it entirely, rather than rendering a second bubble for
    // content the operator's own optimistic echo already put on screen.
    reconcilePendingEchoes(rawRows);

    // McChatModel::fetchMerged()'s own `ORDER BY occurred_at DESC` — newest-first on the wire,
    // same as the initial/Load-older payloads.
    const oldestFirst = [...rawRows].reverse();
    const unseen = selectUnseenRows(oldestFirst, chatSeenKeys);
    if (unseen.length === 0) return; // Idle tick — nothing new, mirrors feed.ts's D-02 precedent.

    recordSeenKeys(unseen.map((row) => row.row_key), chatSeenKeys);
    for (const row of unseen) {
        if (chatNewestSeenMs === null || row.occurred_at_ms > chatNewestSeenMs) {
            chatNewestSeenMs = row.occurred_at_ms;
        }
    }

    // UI-SPEC §8.9: content-less rows are silently omitted here too.
    const withContent = unseen.filter((row) => typeof row.content === 'string' && row.content !== '');
    if (withContent.length === 0) return;

    const rowsContainer = section.querySelector<HTMLElement>('.mc-chatlog-rows');
    const thread = section.querySelector<HTMLElement>('.mc-chatlog-thread');
    if (!rowsContainer || !thread) return;

    // Captured BEFORE appending — UI-SPEC §8.7's own auto-follow-vs-queue decision.
    const followBottom = shouldAutoFollow(thread.scrollHeight, thread.scrollTop, thread.clientHeight);

    appendClusters(rowsContainer, withContent);
    enforceThreadRowCap(section, rowsContainer);

    if (followBottom) {
        thread.scrollTop = thread.scrollHeight;
    } else {
        chatQueuedCount += withContent.length;
        updateNewMessagesPill(section);
    }
}

/**
 * Appends `rows` (already content-filtered, oldest-first) to the bottom of the thread, grouping
 * via the SAME startsNewCluster()/clusterRows() decision the initial render uses — merging into
 * the currently-last on-screen cluster when grouping continues (D-17: a live-arriving message
 * from the same sender within the grouping window must NOT start a redundant new header),
 * otherwise appending brand-new cluster elements built by the SAME buildClusterEl() the initial
 * render/Load-older paths already use. Returns each newly-appended bubble element in row order —
 * Task 2's compose lifecycle needs its own optimistic bubble back.
 */
function appendClusters(rowsContainer: HTMLElement, rows: ChatRowLike[]): HTMLElement[] {
    if (rows.length === 0) return [];

    const clusters = clusterRows(rows);
    const appendedBubbles: HTMLElement[] = [];
    let startIdx = 0;

    if (chatLastRow !== null && chatLastClusterEl !== null && !startsNewCluster(chatLastRow, rows[0])) {
        const firstCluster = clusters[0];
        const outgoing = firstCluster.kind === 'say';
        const senderLabel = outgoing ? CHAT_OUTGOING_SENDER_LABEL : playerDisplayName(firstCluster.player_name);
        // Checkpoint polish: incoming clusters now nest their bubbles inside a
        // .mc-chatlog-cluster-body (the avatar column's sibling), never directly on the cluster
        // row itself. Outgoing clusters have no such wrapper — the cluster element IS the bubble
        // column — so this falls back to chatLastClusterEl unchanged for that case.
        const target =
            chatLastClusterEl.querySelector<HTMLElement>('.mc-chatlog-cluster-body') ?? chatLastClusterEl;

        // Checkpoint polish (connected-stack bubble grouping): the bubble that was previously
        // the cluster's LAST one is no longer the end of the run — downgrade its corner-radius
        // class before appending anything new. 'only' (no modifier class yet) becomes 'first';
        // an existing '--last' becomes '--middle'. target's own last element child is always a
        // bubble here — buildClusterEl() never leaves this branch's target empty.
        const prevLastBubble = target.lastElementChild as HTMLElement | null;
        if (prevLastBubble?.classList.contains('mc-chatlog-bubble')) {
            if (prevLastBubble.classList.contains('mc-chatlog-bubble--last')) {
                prevLastBubble.classList.replace('mc-chatlog-bubble--last', 'mc-chatlog-bubble--middle');
            } else {
                prevLastBubble.classList.add('mc-chatlog-bubble--first');
            }
        }

        for (let i = 0; i < firstCluster.rows.length; i++) {
            const isNewLast = i === firstCluster.rows.length - 1;
            const bubble = buildBubbleEl(firstCluster.rows[i], outgoing, senderLabel, isNewLast ? 'last' : 'middle');
            target.appendChild(bubble);
            appendedBubbles.push(bubble);
        }

        // The meta row's timestamp reflects the LAST row in the (now-extended) cluster — replace
        // the existing time slot inside the meta row rather than appending a trailing block.
        const lastRow = firstCluster.rows[firstCluster.rows.length - 1];
        target.querySelector('.mc-chatlog-cluster-meta .mc-chatlog-time')?.replaceWith(buildMetaTimeEl(lastRow));

        chatLastRow = lastRow;
        startIdx = 1;
    }

    for (let i = startIdx; i < clusters.length; i++) {
        const cluster = clusters[i];
        const firstMs = cluster.rows[0].occurred_at_ms;
        const prevDay = chatLastClusterEl?.dataset.day ?? null;
        if (prevDay === null || localDayKey(firstMs) !== prevDay) {
            rowsContainer.appendChild(dayDividerEl(firstMs));
        }

        const clusterEl = buildClusterEl(cluster);
        rowsContainer.appendChild(clusterEl);
        appendedBubbles.push(...Array.from(clusterEl.querySelectorAll<HTMLElement>('.mc-chatlog-bubble')));

        chatLastClusterEl = clusterEl;
        chatLastRow = cluster.rows[cluster.rows.length - 1];
    }

    return appendedBubbles;
}

/**
 * Trims whole OLDEST clusters (never an individual bubble from the middle, which would orphan
 * that cluster's shared avatar/name header) once the in-DOM bubble count exceeds
 * `THREAD_ROW_CAP`, then re-enables Load-older so the trimmed history stays reachable via a
 * fresh fetch rather than becoming permanently lost from this tab.
 */
function enforceThreadRowCap(section: HTMLElement, rowsContainer: HTMLElement): void {
    let bubbleCount = rowsContainer.querySelectorAll('.mc-chatlog-bubble').length;
    if (bubbleCount <= THREAD_ROW_CAP) return;

    let trimmedAny = false;
    while (bubbleCount > THREAD_ROW_CAP) {
        const oldestCluster = rowsContainer.querySelector<HTMLElement>('.mc-chatlog-cluster');
        if (!oldestCluster) break;
        bubbleCount -= oldestCluster.querySelectorAll('.mc-chatlog-bubble').length;
        oldestCluster.remove();
        trimmedAny = true;
    }

    if (trimmedAny) {
        chatHasMore = true;
        chatLoadOlderExhausted = false;
        updateLoadOlderVisibility(section);
    }
}

// ---------------------------------------------------------------------------------------
// Compose box (D-11, D-12, D-13, UI-SPEC §8.5, §8.6) — optimistic-echo-then-ack, a direct port
// of controls.ts's own bindCommandForm()/appendCommandEcho()/postCommand()/setAck() triad,
// re-expressed as a bubble instead of a monospace `> command` line. D-12: this whole module only
// ever posts the raw message text under a single `message` field — the backend alone builds the
// broadcast-command string, and this module never constructs or shows that prefix.
// ---------------------------------------------------------------------------------------

function bindCompose(section: HTMLElement): void {
    const form = section.querySelector<HTMLFormElement>('.mc-chatlog-compose-form');
    const input = section.querySelector<HTMLInputElement>('.mc-chatlog-compose-input');
    // Absent for an anonymous visitor or an archived server — detail.latte (Plan 251-07) only
    // renders this form when logged in AND not archived. The expected path, not an error.
    if (!form || !input) return;

    const sendUrl = form.dataset.chatlogSendUrl ?? '';
    if (!sendUrl) return;

    form.addEventListener('submit', (e) => {
        e.preventDefault();

        const message = input.value.trim();
        if (!message) return;

        // Responsiveness the optimistic pattern buys — cleared BEFORE the request fires,
        // mirroring bindCommandForm()'s own ordering (controls.ts).
        input.value = '';

        const rowsContainer = section.querySelector<HTMLElement>('.mc-chatlog-rows');
        const thread = section.querySelector<HTMLElement>('.mc-chatlog-thread');
        if (!rowsContainer || !thread) return;

        const sentAtMs = Date.now();
        // A synthetic row, shaped exactly like a real ChatRowLike so it can flow through the
        // SAME appendClusters()/buildClusterEl()/buildBubbleEl() path every other growth path
        // uses (D-17: a rapid burst of operator messages must still group). row_key is a
        // client-local placeholder only — never added to chatSeenKeys, since it is not a real
        // server identity and must never suppress the real chat_say row that later confirms it.
        const optimisticRow: ChatRowLike = {
            row_key: `pending:${sentAtMs}:${Math.random().toString(36).slice(2)}`,
            kind: 'say',
            occurred_at: new Date(sentAtMs).toISOString(),
            occurred_at_ms: sentAtMs,
            player_uuid: null,
            player_name: null,
            content: message,
        };

        const [bubbleEl] = appendClusters(rowsContainer, [optimisticRow]);
        if (!bubbleEl) return;

        // Direct port of appendCommandEcho()'s style.opacity = '0.6' provisional state — no
        // check/cross yet.
        bubbleEl.classList.add('mc-chatlog-bubble--pending');
        const ackEl = document.createElement('span');
        ackEl.className = 'mc-chatlog-ack';
        bubbleEl.appendChild(ackEl);

        // Unconditional, unlike the tail path's near-bottom-only auto-follow — the operator just
        // acted, so following their own send is expected even if they had scrolled up.
        thread.scrollTop = thread.scrollHeight;

        void postChatMessage(sendUrl, message, sentAtMs, bubbleEl, ackEl);
    });
}

function postChatMessage(
    url: string,
    message: string,
    sentAtMs: number,
    bubbleEl: HTMLElement,
    ackEl: HTMLElement,
): Promise<void> {
    const body = new URLSearchParams();
    // D-12: message text only — copied from postCommand()'s own header/body shape, with
    // `command` swapped for `message`.
    body.set('message', message);

    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
    })
        .then((r) => r.json())
        .then((data: { ok?: boolean; message?: string }) => {
            bubbleEl.classList.remove('mc-chatlog-bubble--pending');
            if (data.ok === true) {
                // T-230-29 precedent — the authoritative ack comes from the POST's own {ok}
                // result, never the echo itself.
                ackEl.textContent = ' ✓'; // ✓
                ackEl.classList.add('text-success');
                // T-251-43: only a successful send can ever be reconciled against a real
                // chat_say row — McChatModel::fetchMerged() structurally excludes failed-outcome
                // audit rows from the thread (RESEARCH Open Question 2), so a failed attempt has
                // nothing on the wire to ever match against.
                chatPendingEchoes.push({ content: message, sentAtMs, bubbleEl });
            } else {
                markSendFailed(ackEl);
            }
        })
        .catch(() => {
            bubbleEl.classList.remove('mc-chatlog-bubble--pending');
            markSendFailed(ackEl);
        });
}

/**
 * The single shared failure path for BOTH an `{ok: false}` JSON response and a network
 * rejection — one call site for the locked toast copy (the plan's own acceptance criterion
 * pins this string to exactly one occurrence in this module). Never removes the bubble — matches
 * the console echo's own "keep the failed line, mark it" behaviour (UI-SPEC §8.6). The toast
 * copy is deliberately distinct from the generic console-form failure string, since reusing that
 * one would leak the raw-command framing D-12 deliberately hides from the operator.
 */
function markSendFailed(ackEl: HTMLElement): void {
    ackEl.textContent = ' ✗'; // ✗
    ackEl.classList.add('text-danger');
    showToast('Message failed to send.', 'error');
}

/**
 * T-251-43 — matches an incoming `say` row against a pending optimistic echo by content + a
 * short time window, marking the SERVER row's own `row_key` as already-seen (via chatSeenKeys)
 * so the normal render pipeline skips it, rather than appending a second bubble for content
 * already on screen. Expires unmatched echoes after PENDING_ECHO_TTL_MS regardless of match —
 * an echo this old is treated as any other tail row from then on.
 */
function reconcilePendingEchoes(rawRows: ChatRowLike[]): void {
    if (chatPendingEchoes.length === 0) return;

    const now = Date.now();
    chatPendingEchoes = chatPendingEchoes.filter((echo) => now - echo.sentAtMs <= PENDING_ECHO_TTL_MS);
    if (chatPendingEchoes.length === 0) return;

    for (const row of rawRows) {
        if (row.kind !== 'say') continue;

        const matchIdx = chatPendingEchoes.findIndex(
            (echo) =>
                echo.content === row.content &&
                Math.abs(row.occurred_at_ms - echo.sentAtMs) <= PENDING_ECHO_TTL_MS,
        );
        if (matchIdx === -1) continue;

        const [echo] = chatPendingEchoes.splice(matchIdx, 1);
        chatSeenKeys.add(row.row_key);
        // On a match, the bubble's tracked identity is updated AND, when the server's own row
        // carries narrative_segments, its content is re-rendered from that row (Phase 274 D-13):
        // the raw round-trip text swaps to the parsed rendering without a second bubble ever
        // being created. A plain-text say row's echo keeps this bookkeeping-only behaviour
        // unchanged — no visible change to any message that is not a waypoint.
        echo.bubbleEl.dataset.chatRowKey = row.row_key;
        if (Array.isArray(row.narrative_segments) && row.narrative_segments.length > 0) {
            renderBubbleContent(echo.bubbleEl, row, CHAT_OUTGOING_SENDER_LABEL);
        }
    }
}

// ---------------------------------------------------------------------------------------
// Error rendering
// ---------------------------------------------------------------------------------------

/** Locked copy, 251-UI-SPEC.md's Copywriting Contract — the same heading+body error pattern
 *  leaderboard.ts/profile.ts already use for a failed refresh. Not a toast — the toast is
 *  reserved for the compose-send failure (Plan 251-09). */
function renderFetchError(section: HTMLElement): void {
    const errorEl = section.querySelector<HTMLElement>('.mc-chatlog-error');
    if (!errorEl) return;

    errorEl.replaceChildren();
    errorEl.hidden = false;

    const heading = document.createElement('strong');
    heading.textContent = "Couldn't load this chat";
    errorEl.appendChild(heading);

    errorEl.appendChild(document.createElement('br'));
    errorEl.appendChild(
        document.createTextNode('The chat could not be refreshed just now. Reload the page to try again.'),
    );
}
